// date-fns v2 needs the V2 adapter since MUI X v8.
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useStyles, StyledTextField } from './TimePicker.styles';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { translations } from 'app/locales/i18n';

// Before the MUI v4 -> v9 migration these pickers could not see the app's v4
// theme and rendered with MUI's default theme. Keep that, so app-wide overrides
// (e.g. the 59px `caption` used by the AM/PM buttons) don't leak into them.
const pickersTheme = createTheme();

interface ITimePicker<
  TName extends string,
  TFieldValues extends FieldValues = FieldValues,
> {
  name: TName;
  onChange?: (value: any) => void;
  minTime?: Date;
  maxTime?: Date;
  error?: boolean;
  control?: Control<TFieldValues>;
  helperText?: React.ReactNode;
  setError?: (name: TName, data: { message?: string }) => void;
}

export const TimePicker = <
  TName extends string,
  TFieldValues extends FieldValues = FieldValues,
>({
  error,
  helperText,
  name,
  control,
  maxTime,
  minTime,
  onChange,
  setError,
}: ITimePicker<TName, TFieldValues>) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Keep the analog clock that MUI X v5 used before digital clock lists became the default.
  const clockViewRenderers = {
    hours: renderTimeViewClock,
    minutes: renderTimeViewClock,
    seconds: renderTimeViewClock,
  };

  const textFieldSlotProps = {
    textField: {
      classes: { root: classes.root },
      error,
      helperText,
      fullWidth: true,
      slotProps: {
        formHelperText: { classes: { contained: classes.contained } },
      },
    },
  };

  return (
    <div>
      <ThemeProvider theme={pickersTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {minTime?.getDate() !== maxTime?.getDate() ? (
            <Controller
              render={({ field: { onChange: change, value } }) => (
                <DateTimePicker
                  value={value}
                  minDateTime={minTime}
                  maxDateTime={maxTime}
                  onChange={value => {
                    let newValue = value;
                    let error = false;

                    if (
                      minTime &&
                      value &&
                      minTime.getTime() > value.getTime()
                    ) {
                      newValue = minTime;
                      setError?.(name, {
                        message: t(
                          translations.addManualTestData.timeBeforeError,
                        ),
                      });
                      error = true;
                    }
                    if (
                      maxTime &&
                      value &&
                      value.getTime() > maxTime.getTime()
                    ) {
                      newValue = maxTime;
                      setError?.(name, {
                        message: t(
                          translations.addManualTestData.timeAfterError,
                        ),
                      });
                      error = true;
                    }

                    if (newValue && isNaN(newValue.getTime())) {
                      setError?.(name, {
                        message: t(translations.addManualTestData.invalidDate),
                      });
                      error = true;
                    }
                    if (!error) {
                      setError?.(name, { message: '' });
                    }

                    change(newValue);
                    onChange?.(newValue);
                  }}
                  viewRenderers={clockViewRenderers}
                  slots={{ textField: StyledTextField }}
                  slotProps={textFieldSlotProps}
                />
              )}
              name={name as unknown as Path<TFieldValues>}
              control={control}
            />
          ) : (
            <Controller
              render={({ field: { onChange: change, value } }) => (
                <DesktopTimePicker
                  value={value}
                  minTime={minTime}
                  maxTime={maxTime}
                  onChange={value => {
                    let newValue = value;
                    let error = false;
                    if (
                      minTime &&
                      value &&
                      minTime.getTime() > value.getTime()
                    ) {
                      newValue = minTime;
                      setError?.(name, {
                        message: t(
                          translations.addManualTestData.timeBeforeError,
                        ),
                      });
                      error = true;
                    }
                    if (
                      maxTime &&
                      value &&
                      value.getTime() > maxTime.getTime()
                    ) {
                      newValue = maxTime;
                      setError?.(name, {
                        message: t(
                          translations.addManualTestData.timeAfterError,
                        ),
                      });
                      error = true;
                    }

                    if (newValue && isNaN(newValue.getTime())) {
                      setError?.(name, {
                        message: t(translations.addManualTestData.invalidDate),
                      });
                      error = true;
                    }
                    if (!error) {
                      setError?.(name, { message: '' });
                    }

                    change(newValue);
                    onChange?.(newValue);
                  }}
                  viewRenderers={clockViewRenderers}
                  slots={{ textField: StyledTextField }}
                  slotProps={textFieldSlotProps}
                />
              )}
              name={name as unknown as Path<TFieldValues>}
              control={control}
            />
          )}
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
};
